import * as React from "react";
import { Loader2 } from "lucide-react";
import QueryBar, {
  type QueryBarHandle,
  type QueryBarSelection,
} from "../../shared/components/QueryBar";
import { Button } from "@finster/ui";
import { enhancePrompt } from "../../shared/api/finster";
import {
  isAddinYourFilesUploadEnabled,
  type FeatureFlags,
  type FinsterQueryDataSource,
} from "../../shared/api/finster/constants";
import { ComposerAttachmentPicker } from "../../shared/components/ComposerAttachmentPicker";
import { ComposerAttachmentChipRow } from "../../shared/components/ComposerAttachmentChipRow";
import {
  ComposerUploadBoundary,
  ComposerUploadChips,
} from "../../shared/components/composerUploads";
import type { SuggestedSkillsSelectionState } from "../../shared/components/GettingStartedSkills";
import { createSkillsPickerMenuAction } from "../../shared/components/skillsPickerMenuAction";
import { useDataSourcesPickerMenuAction } from "../../shared/components/useDataSourcesPickerMenuAction";
import { SelectedUserFileChips } from "../../shared/components/SelectedUserFileChips";
import {
  findEnabledSkillByPath,
  getSkillPromptInsertion,
  parseResolvedSkillTokensFromPrompt,
  parseSelectedSkillsFromPrompt,
  MatchingSkillBannerSlot,
  SkillSlashSuggestions,
  SkillsView,
  useSkillSlashController,
  useSkillsController,
  MAX_SELECTED_SKILLS,
  type SkillDecisionSkill,
  type SkillSubmissionOptions,
  type SkillSummary,
} from "../../shared/skills";
import type { MatchingSkillBannerController } from "../../shared/skills/hooks/useMatchingSkillBanner";
import type { SelectedSkillReference } from "../../shared/skills";
import { isExcelClaudeOpusEnabled } from "../../shared/featureFlags/excelExecutorModel";
import { ModelSettingsPicker } from "../../shared/modelPicker/ModelSettingsPicker";
import {
  readComposerDraft,
  saveComposerDraft,
} from "../../shared/clientUpdate/composerDraftStorage";
import {
  FLAGGED_EXECUTOR_MODEL_ID,
  resolveExecutorModel,
} from "../providers/registry";

interface ExcelQueryComposerProps {
  isLoading: boolean;
  /**
   * Resolves `false` when the host refused the submission, which keeps the
   * prompt-scoped Skill selection intact for a retry. The effective file ids
   * are captured in the same event so Skill attachments cannot race the
   * host-state synchronization effect.
   *
   * A host that decides asynchronously must resolve at the point the request is
   * owned, not when the answer arrives — the chips cannot wait out a run.
   */
  onSubmit: (
    query: string,
    skills?: SkillSubmissionOptions,
    userFileIds?: readonly string[],
  ) => boolean | void | Promise<boolean | void>;
  onCancel?: () => void;
  selectedDataSources: readonly FinsterQueryDataSource[];
  onDataSourcesChange: (dataSources: FinsterQueryDataSource[]) => void;
  featureFlags?: FeatureFlags;
  featureFlagsLoading?: boolean;
  selectedUserFileIds?: readonly string[];
  onUserFileIdsChange?: (fileIds: string[]) => void;
  manageSkillsUrl?: string;
  /**
   * Clears the prompt-scoped selection when it changes. Callers pass an
   * account/workbook/tab identity so a Skill can never follow the user into a
   * different workbook or another user's session.
   */
  skillsResetKey?: string;
  /** Optional speculative-prefetch hook. Forwarded straight to
   *  `QueryBar.onValueChange` — see the prop doc there for the rationale. */
  onValueChange?: (value: string) => void;
  onSuggestedSkillsStateChange?: (state: SuggestedSkillsSelectionState) => void;
  matchingSkillBanner?: MatchingSkillBannerController;
  onReplaceSkillRun?: (input: {
    prompt: string;
    selectedSkills: readonly SelectedSkillReference[];
  }) => Promise<boolean>;
}

export interface ExcelQueryComposerHandle {
  selectSuggestedSkill: (path: string) => boolean;
}

const EMPTY_COMPOSER_SELECTION: QueryBarSelection = { start: 0, end: 0, direction: "none" };
const NO_USER_FILE_IDS: readonly string[] = [];

function getDefaultExcelModelSettings() {
  const model = resolveExecutorModel(
    isExcelClaudeOpusEnabled() ? FLAGGED_EXECUTOR_MODEL_ID : null
  );
  return {
    model: model.model,
    effort: model.reasoningEffort,
    ...(model.reasoningMode ? { mode: model.reasoningMode } : {}),
  };
}

export const ExcelQueryComposer = React.forwardRef<
  ExcelQueryComposerHandle,
  ExcelQueryComposerProps
>(function ExcelQueryComposer(
  {
    isLoading,
    onSubmit,
    onCancel,
    selectedDataSources,
    onDataSourcesChange,
    featureFlags,
    featureFlagsLoading = false,
    selectedUserFileIds,
    onUserFileIdsChange,
    manageSkillsUrl,
    skillsResetKey,
    onValueChange,
    onSuggestedSkillsStateChange,
    matchingSkillBanner,
    onReplaceSkillRun,
  },
  ref
) {
  const showUserFileSelector = Boolean(selectedUserFileIds && onUserFileIdsChange);

  const queryBarRef = React.useRef<QueryBarHandle>(null);
  const [composerValue, setComposerValue] = React.useState(() =>
    readComposerDraft("excel", skillsResetKey ?? "")
  );
  const [composerSelection, setComposerSelection] =
    React.useState<QueryBarSelection>(EMPTY_COMPOSER_SELECTION);
  const [skillsViewOpen, setSkillsViewOpen] = React.useState(false);
  /**
   * Identity whose composer state has finished resetting. Passive effects run
   * after a render is committed, so between the render that first sees a new
   * `skillsResetKey` and the reset below, the draft and everything the
   * controller holds still belong to the account, workbook or chat the user
   * just left. Masking them for that render — rather than only clearing them
   * afterwards — is what keeps the previous tenant's prompt and attachments
   * out of the next account's first painted frame.
   */
  const settledSkillsResetKeyRef = React.useRef(skillsResetKey);
  const skillsResetPending = settledSkillsResetKeyRef.current !== skillsResetKey;
  React.useEffect(() => {
    if (settledSkillsResetKeyRef.current === skillsResetKey) return;
    settledSkillsResetKeyRef.current = skillsResetKey;
    const restoredDraft = readComposerDraft("excel", skillsResetKey ?? "");
    setComposerValue(restoredDraft);
    setComposerSelection(EMPTY_COMPOSER_SELECTION);
    onValueChange?.(restoredDraft);
  }, [onValueChange, skillsResetKey]);
  const composerDraft = skillsResetPending ? "" : composerValue;

  const skillsController = useSkillsController({
    resetKey: skillsResetKey,
    visibleUserFileIds: selectedUserFileIds,
    browseOpen: skillsViewOpen,
  });
  const resolvedPromptSkillTokens = React.useMemo(
    () => parseResolvedSkillTokensFromPrompt(composerDraft, skillsController.skills),
    [composerDraft, skillsController.skills]
  );
  const resolvedPromptSkillsKey = resolvedPromptSkillTokens
    .map(({ skill }) => `${skill.id}:${skill.slug}`)
    .join("\0");
  React.useEffect(() => {
    skillsController.replaceSelectedSkills(resolvedPromptSkillTokens.map(({ skill }) => skill));
  }, [resolvedPromptSkillsKey]);
  const insertSkillIntoPrompt = React.useCallback(
    (skill: SkillSummary): boolean => {
      const queryBar = queryBarRef.current;
      if (!queryBar || !skill.enabled) return false;
      const value = queryBar.getValue();
      const selected = parseSelectedSkillsFromPrompt(value, skillsController.skills);
      if (
        selected.length >= MAX_SELECTED_SKILLS ||
        selected.some(
          (selectedSkill) =>
            selectedSkill.id === skill.id || selectedSkill.slug === skill.slug
        )
      ) {
        return false;
      }
      const selection = queryBar.getSelection();
      const insertion = getSkillPromptInsertion(
        value,
        selection.start,
        selection.end,
        skill.slug
      );
      queryBar.replaceRange(insertion.replacement, insertion.start, insertion.end, {
        focus: true,
        selectionMode: "end",
      });
      return true;
    },
    [skillsController.skills]
  );
  const availableSkillSlugsKey = skillsController.skills.map((skill) => skill.slug).join("\0");
  const selectedSkillSlugsKey = skillsController.selectedSkills
    .map((skill) => skill.slug)
    .join("\0");
  const suggestedSkillsState = React.useMemo<SuggestedSkillsSelectionState>(
    () => ({
      availableSkillSlugs: availableSkillSlugsKey ? availableSkillSlugsKey.split("\0") : [],
      selectedSkillSlugs: selectedSkillSlugsKey ? selectedSkillSlugsKey.split("\0") : [],
      selectionAtLimit: skillsController.selectionAtLimit,
    }),
    [availableSkillSlugsKey, selectedSkillSlugsKey, skillsController.selectionAtLimit]
  );
  React.useEffect(() => {
    onSuggestedSkillsStateChange?.(suggestedSkillsState);
  }, [onSuggestedSkillsStateChange, suggestedSkillsState]);
  React.useImperativeHandle(
    ref,
    () => ({
      selectSuggestedSkill: (path: string) => {
        const skill = findEnabledSkillByPath(skillsController.skills, path);
        return skill ? insertSkillIntoPrompt(skill) : false;
      },
    }),
    [insertSkillIntoPrompt, skillsController.skills]
  );

  // Files a Skill contributed are only visible through the controller, so the
  // whole composer — picker, prompt tokens, and submitted research scope — has to
  // read the effective selection rather than the host's manual one.
  const effectiveUserFileIds = skillsResetPending
    ? (selectedUserFileIds ?? NO_USER_FILE_IDS)
    : skillsController.effectiveUserFileIds;

  React.useEffect(() => {
    if (!selectedUserFileIds || !onUserFileIdsChange) return;
    // The controller's selection is still the previous identity's until its
    // own reset lands. Writing it back now would persist one account's or
    // workbook's attachments as the next one's manual choice.
    if (skillsResetPending) return;
    // Only files this chat owns are written back. A Skill's files stay
    // prompt-scoped until the request that used them is accepted, at which
    // point they are promoted into this manual selection — so a Skill the user
    // is still composing with cannot be read back as another workbook's or
    // chat's own selection, while the files a sent request used stay available
    // to its follow-ups.
    const nextFileIds = skillsController.promptFileSelection.manualFileIds;
    const unchanged =
      nextFileIds.length === selectedUserFileIds.length &&
      nextFileIds.every((fileId, index) => fileId === selectedUserFileIds[index]);
    if (!unchanged) onUserFileIdsChange([...nextFileIds]);
  }, [
    onUserFileIdsChange,
    selectedUserFileIds,
    skillsResetPending,
    skillsController.promptFileSelection.manualFileIds,
  ]);

  const handleVisibleUserFileIdsChange = (fileIds: string[]) => {
    skillsController.replaceVisibleUserFileIds(fileIds);
  };

  const handleRemoveUserFileId = (fileId: string) => {
    skillsController.removeUserFileId(fileId);
  };

  const replaceComposerRange = React.useCallback<QueryBarHandle["replaceRange"]>(
    (replacement, start, end, options) => {
      queryBarRef.current?.replaceRange(replacement, start, end, options);
    },
    []
  );
  const focusComposer = React.useCallback<QueryBarHandle["focus"]>((options) => {
    queryBarRef.current?.focus(options);
  }, []);
  const skillSlashController = useSkillSlashController({
    value: composerDraft,
    selection: skillsResetPending ? EMPTY_COMPOSER_SELECTION : composerSelection,
    skills: skillsController.skills,
    selectedSkills: skillsController.selectedSkills,
    replaceRange: replaceComposerRange,
    focus: focusComposer,
    disabled: isLoading,
  });

  // The submitted selection is read from the prompt that is being sent rather
  // than from state that may have moved on, matching how the research scope is
  // captured at submit.
  //
  // The outcome is passed back up so the prompt text settles on the same
  // answer as the Skill selection. A refusal used to clear the prompt anyway,
  // leaving the user to retype a request that was never started.
  const handleSubmit = async (query: string): Promise<boolean> => {
    // Submitting mid-hydration would send a Skill without the files it
    // attached, so the run has to wait for the selection to settle.
    if (skillsController.fileHydrationBlocking) return false;
    const submittedSkills = parseSelectedSkillsFromPrompt(query, skillsController.skills);
    // Taken before the host is asked, so the files are the ones this request
    // carried rather than whatever the composer holds once it answers.
    const submittedSkillFiles = skillsController.captureSubmittedSkillFiles(
      submittedSkills.map((skill) => skill.id)
    );
    const accepted =
      (await onSubmit(
        query,
        {
          selectedSkills: submittedSkills,
          // An empty prompt can mean two different things, and a continuation
          // inherits its frozen Skill only for one of them: the user never
          // touched the picker. Removing every chip is a choice, so the flag
          // comes from the controller rather than from the chips that are left.
          selectionProvided: submittedSkills.length > 0 || skillsController.selectionProvided,
        },
        [...effectiveUserFileIds],
      )) !== false;
    if (accepted) {
      // The Skill chip is gone after this send, but the files it attached stay
      // with the conversation that used them, so a follow-up here still reaches
      // them. Order matters: the reset below keeps the manual selection, which
      // is what these files have just become.
      skillsController.retainAcceptedSkillFiles(submittedSkillFiles);
      skillsController.resetSelectedSkills();
    }
    return accepted;
  };

  const dataSourcesMenuAction = useDataSourcesPickerMenuAction({
    selectedDataSources,
    onDataSourcesChange,
    featureFlags,
    featureFlagsLoading,
  });

  const renderAttachmentPicker = (disabled: boolean) => {
    const menuActions = [
      dataSourcesMenuAction,
      createSkillsPickerMenuAction({
        controller: skillsController,
        onSelectSkill: insertSkillIntoPrompt,
        onExploreSkills: () => setSkillsViewOpen(true),
      }),
    ];

    return (
      <ComposerAttachmentPicker
        selectedFileIds={effectiveUserFileIds}
        onSelectedFileIdsChange={handleVisibleUserFileIdsChange}
        disabled={disabled}
        uploadEnabled={isAddinYourFilesUploadEnabled(featureFlags, featureFlagsLoading)}
        menuActions={menuActions}
      />
    );
  };

  const renderSkillFileStatus = (disabled: boolean) =>
    skillsController.skillFileHydrations.map((hydration) => {
      if (hydration.status === "loading") {
        return (
          <div
            key={hydration.skill.id}
            role="status"
            className="flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground"
          >
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Preparing attached files from /{hydration.skill.slug}…
          </div>
        );
      }
      if (hydration.status === "error") {
        return (
          <div
            key={hydration.skill.id}
            role="alert"
            className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive"
          >
            <span className="min-w-0 flex-1">
              Files from /{hydration.skill.slug}:{" "}
              {hydration.error?.message ?? "Attached files could not be prepared."}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => skillsController.retrySkillFileHydration(hydration.skill.id)}
              disabled={disabled}
              className="h-7 px-2 text-xs"
            >
              Retry
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => skillsController.removeSelectedSkill(hydration.skill)}
              disabled={disabled}
              className="h-7 px-2 text-xs"
            >
              Remove Skill
            </Button>
          </div>
        );
      }
      if (hydration.skippedFileCount > 0) {
        return (
          <div
            key={hydration.skill.id}
            role="status"
            className="rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground"
          >
            {hydration.skippedFileCount} file
            {hydration.skippedFileCount === 1 ? "" : "s"} attached to /{hydration.skill.slug}{" "}
            {hydration.skippedFileCount === 1 ? "is" : "are"} not ready and{" "}
            {hydration.skippedFileCount === 1 ? "was" : "were"} left out.
          </div>
        );
      }
      return null;
    });

  const handleBannerViewSkill = React.useCallback(
    (skill: SkillDecisionSkill) => {
      skillsController.openDetail(skill);
      setSkillsViewOpen(true);
    },
    [skillsController]
  );

  return (
    // `pt-3` (down from the previous `pt-4`) shaves a touch of dead
    // space above the now-shorter composer so the bottom of the
    // chat surface doesn't end up with an awkward extra gap. The
    // shared `QueryBar` is rendered in its `compact` size variant
    // — see `QueryBar`'s `size` prop comment for the rationale.
    <div className="min-w-0 flex-none pt-3">
      <MatchingSkillBannerSlot
        banner={matchingSkillBanner}
        onViewSkill={handleBannerViewSkill}
        onReplaceSkillRun={onReplaceSkillRun}
      />
      <ComposerUploadBoundary key={skillsResetKey}>
      <QueryBar
        ref={queryBarRef}
        value={composerDraft}
        onValueChange={(value) => {
          setComposerValue(value);
          saveComposerDraft("excel", skillsResetKey ?? "", value);
          onValueChange?.(value);
        }}
        onSelectionChange={setComposerSelection}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onEnhancePrompt={enhancePrompt}
        submitDisabled={skillsController.fileHydrationBlocking}
        submitDisabledReason={
          skillsController.skillFileHydrations.some((hydration) => hydration.status === "error")
            ? "Retry or remove the Skill whose attached files could not be prepared."
            : "Preparing attached Skill files."
        }
        isLoading={isLoading}
        renderTrailingControls={(disabled) => (
          <ModelSettingsPicker
            host="excel"
            getDefaultSelection={getDefaultExcelModelSettings}
            defaultLabel="Excel default"
            disabled={disabled}
          />
        )}
        renderAboveInput={
          showUserFileSelector
            ? (disabled) => (
                <div className="flex min-w-0 flex-col gap-2">
                  <ComposerAttachmentChipRow>
                    <SelectedUserFileChips
                      selectedFileIds={effectiveUserFileIds}
                      fileDescriptionsById={
                        skillsResetPending
                          ? undefined
                          : skillsController.fileDescriptionsByFileId
                      }
                      onRemoveFileId={handleRemoveUserFileId}
                      disabled={disabled}
                    />
                    <ComposerUploadChips disabled={disabled} />
                  </ComposerAttachmentChipRow>
                  {!skillsResetPending && skillsController.fileSelectionError && (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive"
                    >
                      {skillsController.fileSelectionError.message}
                    </div>
                  )}
                  {!skillsResetPending && renderSkillFileStatus(disabled)}
                </div>
              )
            : undefined
        }
        renderLeadingControls={renderAttachmentPicker}
        inputAriaProps={skillSlashController.inputAriaProps}
        onInputKeyDown={skillSlashController.onKeyDown}
        highlightRanges={resolvedPromptSkillTokens.map(({ start, end }) => ({
          start,
          end,
        }))}
        renderInputOverlay={({
          overlayAnchorRef,
        }: {
          overlayAnchorRef: React.RefObject<HTMLDivElement | null>;
        }) => (
          <SkillSlashSuggestions
            open={skillSlashController.open}
            atLimit={skillSlashController.atLimit}
            skills={skillSlashController.suggestions}
            activeIndex={skillSlashController.activeIndex}
            listboxId={skillSlashController.listboxId}
            anchorRef={overlayAnchorRef}
            onActiveIndexChange={skillSlashController.onActiveIndexChange}
            onSelect={skillSlashController.selectSkill}
            onDismiss={skillSlashController.dismiss}
          />
        )}
        size="compact"
      />
      </ComposerUploadBoundary>
      <SkillsView
        open={skillsViewOpen}
        controller={skillsController}
        manageSkillsUrl={manageSkillsUrl}
        onBack={() => {
          skillsController.closeDetail();
          setSkillsViewOpen(false);
        }}
        onClose={() => setSkillsViewOpen(false)}
        onTryInChat={(skill) => {
          if (!insertSkillIntoPrompt(skill)) return;
          setSkillsViewOpen(false);
          window.setTimeout(() => queryBarRef.current?.focus(), 0);
        }}
      />
    </div>
  );
});
